import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../topics/tasks/datepicker.css'; // Import our custom datepicker styles
import { Trash2, X } from 'lucide-react';
import { logger } from '../../utils/logger';
import { calculateOptimalPopupPosition } from '../../lib/utils';
import { useCalendarPreferences } from '../../hooks/useCalendarPreferences';
import { useViewport } from '../../contexts/ViewportContext';
import { useOutsideClickHandler } from '../../hooks/useOutsideClickHandler';
import MobileDatePickerModal from './MobileDatePickerModal';

// Define a union type for the different ways to position the date picker
type PositionSource =
  | { type: 'menuPosition'; position: { top: number; left: number } }
  | { type: 'buttonRef'; ref: React.RefObject<HTMLElement>; positionAtClick?: boolean }
  | { type: 'fullscreen' }; // Added fullscreen option for mobile

interface FloatingDatePickerProps {
  // Common props
  selectedDate: Date | null;
  onClose: () => void;

  // Position can be provided either as a direct position or as a ref to the trigger element
  positionSource: PositionSource;

  // Callback when date changes - can be direct or through task update
  onDateChange: (date: Date | null) => void;

  // Optional clear handler - if not provided, we'll use onDateChange(null)
  onClear?: () => void;

  // Optional CSS class for custom styling
  className?: string;

  // Whether the picker was launched from a menu/modal context (affects mobile display)
  launchedFromMenu?: boolean;
}

const FloatingDatePicker: React.FC<FloatingDatePickerProps> = ({
  selectedDate,
  onClose,
  positionSource,
  onDateChange,
  onClear,
  className = '',
}) => {
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const { datePickerLocale } = useCalendarPreferences();
  const { isMobile } = useViewport();

  // Store the positionSource in a ref to avoid dependency issues
  const positionSourceRef = useRef(positionSource);
  // Update the ref when positionSource changes
  useEffect(() => {
    positionSourceRef.current = positionSource;
  }, [positionSource]);

  // Calculate position for the floating picker - only calculate ONCE on mount
  useEffect(() => {
    if (isMobile) {
      // For mobile, we center horizontally and position at 20% from top
      const viewportHeight = window.innerHeight;
      const topPosition = viewportHeight * 0.2; // 20% from top

      setPosition({
        top: topPosition,
        left: window.innerWidth / 2, // Center horizontally
      });
    } else {
      // For desktop, calculate optimal position based on trigger
      const calculatePosition = () => {
        // Use the ref version to avoid dependency issues
        const currentPositionSource = positionSourceRef.current;
        let triggerPosition: { top: number; left: number };

        if (currentPositionSource.type === 'menuPosition') {
          triggerPosition = {
            top: currentPositionSource.position.top,
            left: currentPositionSource.position.left,
          };
        } else if (
          currentPositionSource.type === 'buttonRef' &&
          currentPositionSource.ref.current
        ) {
          const rect = currentPositionSource.ref.current.getBoundingClientRect();
          // Check if we should position at the button or at its center/click point
          if (currentPositionSource.positionAtClick) {
            // Position at the center of the button for a more natural appearance
            triggerPosition = {
              top: rect.top + rect.height / 2,
              left: rect.left + rect.width / 2,
            };
          } else {
            // Position at the top-left of the button (default)
            triggerPosition = {
              top: rect.top,
              left: rect.left,
            };
          }
        } else {
          // Fallback to center of screen
          triggerPosition = {
            top: window.innerHeight / 2,
            left: window.innerWidth / 2,
          };
        }

        // The datepicker is approximately 350x350 pixels
        const optimalPosition = calculateOptimalPopupPosition(
          triggerPosition,
          { width: 350, height: 350 },
          { x: 0, y: 0 } // No offset to position exactly at cursor location
        );

        setPosition({
          top: optimalPosition.top,
          left: optimalPosition.left,
        });
      };

      // Initial calculation
      calculatePosition();

      // Only recalculate on window resize
      window.addEventListener('resize', calculatePosition);

      return () => {
        window.removeEventListener('resize', calculatePosition);
      };
    }
  }, [isMobile]); // Recalculate if mobile status changes

  // Handle clicks outside the datepicker using the standardized hook
  const buttonRef = positionSource.type === 'buttonRef' ? positionSource.ref : null;
  const excludeRefs = buttonRef ? [buttonRef] : [];

  // Use our standardized outside click handler, but don't react to clicks on the parent button
  useOutsideClickHandler(
    datePickerRef,
    e => {
      // Don't handle outside clicks on mobile - mobile has its own modal with close button
      if (isMobile) {
        return;
      }
      // Make sure we're not closing if the click was on the trigger button
      if (buttonRef?.current && buttonRef.current.contains(e.target as Node)) {
        return; // Don't close if clicked on the trigger button
      }
      onClose();
    },
    true, // always active
    excludeRefs
  );

  // Handle clear button click
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClear) {
      onClear();
    } else {
      onDateChange(null);
    }

    onClose();
  };

  // Handle date selection
  const handleDateChange = (date: Date) => {
    // Create a fresh Date object to avoid any reference issues
    const newDate = new Date(date.getTime());
    onDateChange(newDate);
    onClose();
  };

  // Get content styles based on mobile status
  const getContentStyles = (): React.CSSProperties => {
    if (isMobile) {
      return {
        position: 'fixed',
        top: `${position.top}px`,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '320px', // Fixed width for mobile
        zIndex: 11000,
      };
    }

    // Desktop positioning
    return {
      position: 'fixed',
      top: `${position.top}px`,
      left: `${position.left}px`,
      width: '350px', // Slightly wider for desktop
      zIndex: 11000,
    };
  };

  // For mobile or fullscreen type, use the full-screen modal
  if (isMobile || positionSource.type === 'fullscreen') {
    return (
      <MobileDatePickerModal
        isOpen={true}
        selectedDate={selectedDate}
        onClose={onClose}
        onDateChange={date => {
          // Log the date change
          logger.component('MobileDatePickerModal date changed', {
            newDate: date ? date.toISOString() : null,
            oldDate: selectedDate ? selectedDate.toISOString() : null,
            isMobile,
          });

          // Make sure we call the date change function ONLY ONCE with the right date
          if (date) {
            // Create a fresh copy to ensure we don't have reference issues
            const newDate = new Date(date.getTime());
            // Set hours to noon to avoid timezone issues
            newDate.setHours(12, 0, 0, 0);

            // Call the parent component's handler with the fixed date
            onDateChange(newDate);
          } else {
            // Handle null dates (clearing)
            onDateChange(null);
          }
        }}
        onClear={
          onClear ||
          (() => {
            onDateChange(null);
          })
        }
      />
    );
  }

  const content = (
    <>
      <div
        ref={datePickerRef}
        style={getContentStyles()}
        className={`shadow-lg rounded-lg bg-white border border-gray-200 floating-date-picker overflow-hidden ${className}`}
        // Capture and stop all events from bubbling
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        onMouseUp={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-3 py-2 bg-gray-50 border-b border-gray-200 rounded-t-lg">
          <div>
            {selectedDate && (
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  handleClear(e);
                }}
                className="text-red-500 hover:text-red-700 flex items-center hover:bg-red-50 rounded-md px-2 py-1"
              >
                <Trash2 size={16} className="mr-1" />
                Clear
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              onClose();
            }}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={16} />
          </button>
        </div>

        <DatePicker
          selected={selectedDate}
          onChange={handleDateChange}
          minDate={new Date()} // This ensures selecting past dates is disabled
          inline
          className="w-full"
          locale={datePickerLocale}
          shouldCloseOnSelect={true}
          useWeekdaysShort={true}
          fixedHeight={true}
          wrapperClassName="rounded-b-lg overflow-hidden w-full"
          // Highlight the selected date even if it's in the past
          highlightDates={[
            // Highlight today's date
            {
              'react-datepicker__day--highlighted-today': [new Date()],
            },
            // Highlight the selected date if it exists and is different from today
            ...(selectedDate && selectedDate.getTime() !== new Date().setHours(0, 0, 0, 0)
              ? [
                  {
                    'react-datepicker__day--highlighted-selected': [selectedDate],
                  },
                ]
              : []),
          ]}
          renderDayContents={(day, date) => {
            if (!date) return day;

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // Special handling for past selected date
            if (
              selectedDate &&
              date.getDate() === selectedDate.getDate() &&
              date.getMonth() === selectedDate.getMonth() &&
              date.getFullYear() === selectedDate.getFullYear()
            ) {
              // If this is the currently selected date and it's in the past
              if (date < today) {
                return (
                  <div className="flex items-center justify-center h-full relative">
                    <span className="font-extrabold text-white" style={{ fontSize: '110%' }}>
                      {day}
                    </span>
                  </div>
                );
              }
            }

            return day;
          }}
        />
      </div>
    </>
  );

  // Use portal to render the date picker at the root level to avoid any clipping issues
  return ReactDOM.createPortal(content, document.body);
};

export default FloatingDatePicker;
